import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { getShippingZones as getLocalZones } from './dataStore';

var NOT_CONNECTED = 'Not connected to the database. Changes were not saved.';
var SAVE_FAILED = 'Could not save. Please check your internet connection and try again.';

function normaliseZone(z) {
  return Object.assign({}, z, {
    standard_rate: Number(z.standard_rate),
    express_rate: Number(z.express_rate),
    standard_estimate: z.standard_estimate || '',
    express_estimate: z.express_estimate || ''
  });
}

// ---- Shipping zones ----

export async function fetchShippingZones() {
  if (!supabase) return { zones: getLocalZones(), error: NOT_CONNECTED };
  var result = await supabase.from('shipping_zones').select('*').order('sort_order');
  if (result.error || !result.data || result.data.length === 0) {
    console.error('Could not load shipping zones:', result.error ? result.error.message : 'no rows');
    return { zones: getLocalZones(), error: 'Could not load shipping zones' };
  }
  return { zones: result.data.map(normaliseZone), error: null };
}

export function useShippingZones() {
  var [zones, setZones] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    var cancelled = false;
    fetchShippingZones().then(function(res) {
      if (cancelled) return;
      setZones(res.zones);
      setLoading(false);
    });
    return function() { cancelled = true; };
  }, []);

  return { zones: zones, loading: loading };
}

function isValidRate(value) {
  if (value === '' || value === null || value === undefined) return false;
  var n = Number(value);
  return !isNaN(n) && n >= 0;
}

export async function saveShippingZones(zones) {
  if (!supabase) return { error: NOT_CONNECTED };

  var bad = zones.find(function(z) { return !isValidRate(z.standard_rate) || !isValidRate(z.express_rate); });
  if (bad) return { error: 'Rates for ' + bad.name + ' must be a number (0 or more). Nothing was saved.' };

  var now = new Date().toISOString();
  var results = await Promise.all(zones.map(function(z) {
    return supabase.from('shipping_zones').update({
      countries: z.countries || '',
      standard_rate: Number(z.standard_rate),
      express_rate: Number(z.express_rate),
      standard_estimate: z.standard_estimate || '',
      express_estimate: z.express_estimate || '',
      updated_at: now
    }).eq('id', z.id).select('id');
  }));

  var failed = results.find(function(r) { return r.error || !r.data || r.data.length === 0; });
  if (failed) {
    console.error('Shipping save failed:', failed.error || 'no rows updated');
    return { error: SAVE_FAILED };
  }
  return { error: null };
}

// ---- Store settings (status, free shipping threshold, reopen date) ----

export async function fetchStoreSettings() {
  if (!supabase) return { settings: null, error: NOT_CONNECTED };
  var result = await supabase.from('store_settings').select('*').eq('id', 1).maybeSingle();
  if (result.error || !result.data) {
    console.error('Could not load store settings:', result.error ? result.error.message : 'no row');
    return { settings: null, error: 'Could not load store settings' };
  }
  return { settings: result.data, error: null };
}

export async function saveStoreSettings(settings) {
  if (!supabase) return { error: NOT_CONNECTED };
  var row = { updated_at: new Date().toISOString() };
  ['store_status', 'free_shipping_threshold', 'pause_reopen_date'].forEach(function(key) {
    if (settings[key] !== undefined) row[key] = settings[key];
  });

  var result = await supabase.from('store_settings').update(row).eq('id', 1).select('id');
  if (result.error || !result.data || result.data.length === 0) {
    console.error('Store settings save failed:', result.error || 'no rows updated');
    return { error: SAVE_FAILED };
  }
  return { error: null };
}

// ---- Promo codes ----

var PROMO_PATTERN = /^[A-Z0-9_-]{4,30}$/;

// Checkout: asks the database about ONE code. Never sees the list.
export async function checkPromoCode(code) {
  if (!supabase) return { promo: null, error: "Couldn't check that code right now. Please try again." };
  var result = await supabase.rpc('check_promo_code', { p_code: code });
  if (result.error) {
    console.error('Promo check failed:', result.error.message);
    return { promo: null, error: "Couldn't check that code right now. Please try again." };
  }
  var row = result.data && result.data[0];
  return {
    promo: row ? { code: row.code, discount_percent: Number(row.discount_percent) } : null,
    error: null
  };
}

// Admin only
export async function fetchPromoCodes() {
  if (!supabase) return { promos: [], error: NOT_CONNECTED };
  var result = await supabase
    .from('promo_codes')
    .select('id, code, discount_percent, is_active, expires_on')
    .order('created_at');
  if (result.error) {
    console.error('Could not load promo codes:', result.error.message);
    return { promos: [], error: 'Could not load promo codes' };
  }
  return { promos: result.data, error: null };
}

export async function addPromoCode(code, discount, expiresOn) {
  if (!supabase) return { error: NOT_CONNECTED };
  var clean = String(code || '').trim().toUpperCase();
  var pct = Number(discount);
  if (!PROMO_PATTERN.test(clean)) {
    return { error: 'Codes must be 4 to 30 letters or numbers with no spaces. Dashes are OK.' };
  }
  if (!Number.isInteger(pct) || pct < 1 || pct > 90) {
    return { error: 'Discount must be a whole number from 1 to 90.' };
  }
  var result = await supabase
    .from('promo_codes')
    .insert({ code: clean, discount_percent: pct, expires_on: expiresOn || null })
    .select('id');
  if (result.error) {
    console.error('Add promo failed:', result.error);
    if (result.error.code === '23505') return { error: 'That code already exists. You can turn it back on instead.' };
    return { error: SAVE_FAILED };
  }
  return { error: null };
}

export async function updatePromoCode(id, changes) {
  if (!supabase) return { error: NOT_CONNECTED };
  var row = { updated_at: new Date().toISOString() };
  ['is_active', 'discount_percent', 'expires_on'].forEach(function(key) {
    if (changes[key] !== undefined) row[key] = changes[key];
  });
  var result = await supabase.from('promo_codes').update(row).eq('id', id).select('id');
  if (result.error || !result.data || result.data.length === 0) {
    console.error('Promo update failed:', result.error || 'no rows updated');
    return { error: SAVE_FAILED };
  }
  return { error: null };
}

// ---- Orders (admin) ----

var ADMIN_VISIBLE_STATUSES = ['paid', 'processing', 'shipped', 'delivered', 'cancelled'];

function centsToDollars(cents) {
  return Math.round(Number(cents || 0)) / 100;
}

function normaliseOrder(row) {
  return Object.assign({}, row, {
    guest_name: row.customer_name,
    guest_email: row.email,
    subtotal: centsToDollars(row.subtotal_cents),
    shipping_cost: centsToDollars(row.shipping_cents),
    discount_amount: centsToDollars(row.discount_cents),
    total: centsToDollars(row.total_cents),
    items: (row.items || []).map(function(item) {
      return {
        name: item.name,
        variant: item.variant,
        colour: item.colour,
        quantity: item.quantity,
        price: centsToDollars(item.unit_cents)
      };
    })
  });
}

export async function fetchOrders() {
  if (!supabase) return { orders: [], error: NOT_CONNECTED };
  var result = await supabase
    .from('orders')
    .select('*')
    .in('status', ADMIN_VISIBLE_STATUSES)
    .order('created_at', { ascending: false });
  if (result.error) {
    console.error('Could not load orders:', result.error.message);
    return { orders: [], error: 'Could not load orders' };
  }
  return { orders: result.data.map(normaliseOrder), error: null };
}

export async function updateOrder(id, changes) {
  if (!supabase) return { order: null, error: NOT_CONNECTED };
  var row = {};
  ['status', 'tracking_number', 'admin_notes'].forEach(function(key) {
    if (changes[key] !== undefined) row[key] = changes[key];
  });
  var result = await supabase.from('orders').update(row).eq('id', id).select('*');
  if (result.error) {
    console.error('Order update failed:', result.error);
    if (result.error.code === '23514') return { order: null, error: 'That status change is not allowed for this order.' };
    return { order: null, error: SAVE_FAILED };
  }
  if (!result.data || result.data.length === 0) {
    return { order: null, error: SAVE_FAILED };
  }
  return { order: normaliseOrder(result.data[0]), error: null };
}

// Asks the admin-order-email function to email the customer about their
// order (today: kind 'shipping'). -> { sentTo, alreadySent, error }
export async function sendOrderEmail(orderId, kind) {
  if (!supabase) return { sentTo: null, alreadySent: false, error: 'Not connected to the database, so the email wasn’t sent.' };
  try {
    var res = await supabase.functions.invoke('admin-order-email', { body: { orderId: orderId, kind: kind } });
    if (res.error) {
      var message = null;
      var ctx = res.error.context;
      if (ctx && typeof ctx.json === 'function') {
        try { var body = await ctx.json(); message = body && body.error; } catch (e) { /* not JSON */ }
      }
      if (!message && (res.error.name === 'FunctionsFetchError' || res.error.name === 'FunctionsRelayError')) {
        message = 'The email service couldn’t be reached. Check your internet connection, and that admin-order-email is deployed.';
      }
      if (!message && ctx && ctx.status === 404) message = 'The email service (admin-order-email) isn’t deployed yet.';
      return { sentTo: null, alreadySent: false, error: message || 'The email wasn’t sent.' };
    }
    if (!res.data || res.data.ok !== true) return { sentTo: null, alreadySent: false, error: 'The email wasn’t sent.' };
    return { sentTo: res.data.sentTo || null, alreadySent: res.data.alreadySent === true, error: null };
  } catch (e) {
    console.error('sendOrderEmail failed:', e);
    return { sentTo: null, alreadySent: false, error: 'The email wasn’t sent.' };
  }
}

// ---- Payments ----

var FUNCTIONS_URL = (process.env.REACT_APP_SUPABASE_URL || '') + '/functions/v1/';
export var CHECKOUT_FORM_KEY = 'aml_checkout_form';

export async function createCheckout(payload) {
  try {
    var res = await fetch(FUNCTIONS_URL + 'create-checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    var data = await res.json().catch(function() { return {}; });
    // Only ever send customers to Stripe's own payment pages
    if (data && typeof data.url === 'string' && data.url.indexOf('https://checkout.stripe.com/') === 0) {
      return { url: data.url, error: null };
    }
    return { url: null, error: (data && data.error) || "Couldn't start the payment. Please try again." };
  } catch (e) {
    return { url: null, error: "Couldn't reach the payment service. Please check your internet connection and try again." };
  }
}

export async function fetchOrderStatus(sessionId) {
  try {
    var res = await fetch(FUNCTIONS_URL + 'order-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: sessionId })
    });
    var data = await res.json().catch(function() { return {}; });
    if (res.status === 404) return { order: null, error: 'not_found' };
    if (!res.ok || !data.orderNumber) return { order: null, error: 'unavailable' };
    return { order: data, error: null };
  } catch (e) {
    return { order: null, error: 'unavailable' };
  }
}